import React, { useState } from 'react';
import { capitalize } from 'lodash';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import StandardModal from 'components/shared/StandardModal';
import type { AIPrompt } from 'models/flexible/AIPrompt';
import { updateAIPrompt } from 'services/api/admin/aiPrompts';
import type { AdminAIPromptIndexFields, AdminAIPromptShowFields } from 'services/api/fields';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

type Props = {
  isOpen: boolean;
  workspacePrompt:
    | AIPrompt<AdminAIPromptShowFields>
    | AIPrompt<AdminAIPromptIndexFields<'clientCount' | 'groupCount' | 'config' | 'latestProcessPromptStatus'>>;
  onCancel: () => void;
  onComplete: () => void;
};

export default function WorkspacePromptPublishModal({ isOpen, workspacePrompt, onCancel, onComplete }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const { addToast } = useToastActions();

  if (!isOpen) return null;

  const handlePublish = async () => {
    setSubmitting(true);
    try {
      await updateAIPrompt(workspacePrompt.id, { isPublished: true });
      addToast({ text: 'Workspace prompt published.' });
      onComplete();
    } catch {
      addToast({ text: 'There was an error publishing the workspace prompt. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StandardModal title='Confirm Details to Publish' onCancel={onCancel}>
      <FormContainer>
        <SectionHeader>Review and confirm prompt configuration before publishing</SectionHeader>
        <SummaryRow>
          <SummaryLabel>Prompt Type</SummaryLabel>
          <SummaryValue>{capitalize(workspacePrompt.type || '—')}</SummaryValue>
        </SummaryRow>
        {workspacePrompt.displayName && (
          <SummaryRow>
            <SummaryLabel>Display Name</SummaryLabel>
            <SummaryValue>{workspacePrompt.displayName}</SummaryValue>
          </SummaryRow>
        )}
      </FormContainer>

      <Actions>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handlePublish} disabled={submitting} loading={submitting}>
          Publish Prompt
        </Button>
      </Actions>
    </StandardModal>
  );
}

const FormContainer = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled(BodySmall)`
  margin-bottom: 12px;
  font-weight: 400;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid ${colors.black10};
`;

const SummaryLabel = styled(BodySmallBold)`
  color: ${colors.black50};
`;

const SummaryValue = styled.div`
  font-size: 14px;
`;

const Actions = styled(ButtonGroup)`
  justify-content: flex-end;
`;
